import React, { useMemo, useState } from 'react';

const SortIcon = ({ direction }) => (
  <svg className="w-3 h-3 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
    ) : (
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
    )}
  </svg>
);

function BreakoutTable({ title, signals, tickers }) {
  const [sortDir, setSortDir] = useState('asc');

  const rows = useMemo(() => {
    return signals
      .map((sig) => {
        const live = tickers[sig.symbol];
        const price = live ? live.price : sig.price || null;
        let changePercent = null;
        const refOpen = sig.ref_open || sig.premarket_open || 0;
        if (price && refOpen > 0) {
          changePercent = ((price - refOpen) / refOpen) * 100;
        }
        return { ...sig, livePrice: price, changePercent };
      })
      .sort((a, b) => {
        if (a.changePercent == null && b.changePercent == null) return 0;
        if (a.changePercent == null) return 1;
        if (b.changePercent == null) return -1;
        return sortDir === 'asc'
          ? a.changePercent - b.changePercent
          : b.changePercent - a.changePercent;
      });
  }, [signals, tickers, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">{title}</h2>
          <span className="text-xs text-gray-500">
            {rows.length} {rows.length === 1 ? 'ticker' : 'tickers'}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className="px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors"
                onClick={toggleSort}
              >
                Change %<SortIcon direction={sortDir} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 sm:px-6 py-12 text-center">
                  <span className="text-sm text-gray-500">No breakouts yet</span>
                </td>
              </tr>
            ) : rows.map((row) => {
                const hasPrice = row.livePrice != null;
                const isPositive = row.changePercent >= 0;
                return (
                  <tr
                    key={row.symbol}
                    onClick={() => openTradingView(row.symbol)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{row.symbol}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {hasPrice ? (
                        <span className="text-sm text-gray-300 font-medium tabular-nums">
                          ${row.livePrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.changePercent != null ? (
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {isPositive ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {isPositive ? '+' : ''}
                          {row.changePercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BreakoutTables({ premarketLongs, premarketShorts, mainLongs, mainShorts, tickers }) {
  return (
    <div className="space-y-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakoutTable title="Long Breakouts - Premarket" signals={premarketLongs} tickers={tickers} />
        <BreakoutTable title="Short Breakouts - Premarket" signals={premarketShorts} tickers={tickers} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakoutTable title="Long Breakouts - Main-Market" signals={mainLongs} tickers={tickers} />
        <BreakoutTable title="Short Breakouts - Main-Market" signals={mainShorts} tickers={tickers} />
      </div>
    </div>
  );
}
